import { App as AntdApp, ConfigProvider } from 'antd';
import enGB from 'antd/locale/en_GB';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { buildTheme, type ThemeMode } from './theme.js';
import { paletteVariables } from './tokens.js';
import './global.css';

/** What the user chose. `system` follows the operating system. */
export type ThemePreference = ThemeMode | 'system';

const STORAGE_KEY = 'orbit.theme';

interface ThemeContextValue {
  /** The user's choice, including `system`. */
  preference: ThemePreference;
  /** What is actually being rendered, after resolving `system`. */
  mode: ThemeMode;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
  } catch {
    // Private browsing and hardened settings can make localStorage throw on
    // read. A missing preference is not worth breaking the app over.
    return 'system';
  }
}

function systemMode(): ThemeMode {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Theme, locale and Ant Design's app-level context (message, notification,
 * modal) for the whole tree.
 *
 * `AntdApp` is what makes `App.useApp()` work. Without it, antd's static
 * `message.success(...)` renders outside the ConfigProvider and silently loses
 * the theme — a classic source of "why is this one toast the wrong colour".
 * It renders a real wrapper element rather than `component={false}`, which is
 * required for the CSS-variable theme to reach the toasts it portals.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [resolvedSystem, setResolvedSystem] = useState<ThemeMode>(systemMode);

  // Follow the OS while the preference is `system`.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => {
      setResolvedSystem(event.matches ? 'dark' : 'light');
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const mode: ThemeMode = preference === 'system' ? resolvedSystem : preference;

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not being able to remember the choice is survivable; crashing is not.
    }
  }, []);

  // Let the browser style form controls, scrollbars and the address bar to match.
  useEffect(() => {
    document.documentElement.style.colorScheme = mode;
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  const theme = useMemo(() => buildTheme(mode), [mode]);

  /**
   * The whole colour palette as CSS custom properties for the active mode:
   * all ten Notion families in their text, background and icon variants, plus
   * the five semantic aliases.
   *
   * Components read these via `tone('success')` or `notionVar('purple')`
   * rather than importing hex values, which is what lets a label be correct in
   * both themes without subscribing to the theme context or re-rendering when
   * the mode flips. The string is generated from the same token objects
   * `theme.ts` uses, so the two can never disagree.
   */
  const paletteStyles = useMemo(() => `:root{${paletteVariables(mode)}}`, [mode]);
  const value = useMemo<ThemeContextValue>(
    () => ({ preference, mode, setPreference }),
    [preference, mode, setPreference],
  );

  return (
    <ThemeContext.Provider value={value}>
      <style>{paletteStyles}</style>
      <ConfigProvider
        theme={theme}
        locale={enGB}
        // One place to set density and empty states for every antd component.
        componentSize="middle"
        // Marking OPTIONAL fields rather than required ones is the right
        // default for a 7-part onboarding form where nearly everything is
        // required — asterisks on 30 of 33 fields carry no information.
        form={{ requiredMark: 'optional', scrollToFirstError: true }}
      >
        <AntdApp>{children}</AntdApp>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside <ThemeProvider>');
  return context;
}
